import React, { useState, useCallback } from "react";
import "./upiPayments.css";
import Navbar from "../nav/navbar";
import Nextstep from "../nextStep/nextstep";
import Footer from "../footer/footer";
import { FaArrowRight } from "react-icons/fa";
import { motion, easeInOut } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import { Helmet } from "react-helmet";

// ─── Constants ────────────────────────────────────────────────────────────────

const API_URL = "https://connectedpay.haodapay.com/api/v1/leadstore";

const BUSINESS_TYPES = [
  { value: "ecommerce", label: "E-commerce" },
  { value: "saas", label: "SaaS" },
  { value: "education", label: "Education" },
  { value: "travel", label: "Travel & Hospitality" },
  { value: "logistics", label: "Logistics" },
  { value: "other", label: "Other" },
];

const WHY_CHOOSE_ITEMS = [
  {
    title: "Real-Time Payment Processing",
    desc: "Accept payments instantly with real-time bank-to-bank transfers, powered by T+0 settlements, ensuring faster access to funds and improved liquidity.",
  },
  {
    title: "Secure & Compliant Transactions",
    desc: "Built on a highly secure UPI infrastructure, all transactions are encrypted and compliant with industry standards, ensuring complete protection for both businesses and customers.",
  },
  {
    title: "Smart Dashboard & Reconciliation",
    desc: "Gain complete visibility with a centralized dashboard to track transactions, simplify reconciliation, and generate detailed financial reports.",
  },
  {
    title: "High Success Rate",
    desc: "UPI collections deliver consistently high transaction success rates, minimizing failures and enhancing the overall customer payment experience.",
  },
  {
    title: "Cost-Effective Payment Processing",
    desc: "Reduce operational costs with lower transaction fees compared to traditional payment methods, helping improve overall profitability.",
  },
];

const HOW_IT_WORKS_STEPS = [
  "The customer selects UPI as the preferred payment method",
  "A payment request is generated via QR code, UPI intent, or collect request",
  "The customer authorizes the payment through their UPI app",
  "Funds are credited instantly to your account with T+0 settlement",
];

const USE_CASES = [
  "E-commerce & Marketplaces",
  "NBFCs & Lending Platforms",
  "Education & Training Institutes",
  "Travel & Hospitality",
  "Logistics & Distribution",
  "Bill Payments & Subscription Services",
];

const COLLECTION_MODES = [
  {
    title: "UPI QR Code",
    desc: "Static QR codes require manual entry, which can lead to errors. Dynamic QR codes auto-fill the amount for each transaction—making payments faster and smoother. With API integration, each payment is assigned a unique reference for easy automatic reconciliation.",
  },
  {
    title: "UPI Collect Request",
    desc: "Send payment requests directly to customers' UPI apps. They review and approve using their UPI PIN. Ideal for subscriptions, renewals, and invoice payments.",
  },
  {
    title: "UPI Intent (Deep Link)",
    desc: "Redirect users to their preferred UPI app with pre-filled details in one click. A fast and seamless experience, especially for mobile payments and app-based checkouts.",
  },
  {
    title: "UPI AutoPay (Recurring Payments)",
    desc: "Enable automatic recurring payments with one-time customer approval. Perfect for subscriptions, EMIs, and SaaS billing.",
  },
  {
    title: "Payment Links",
    desc: "Share simple payment links via SMS, WhatsApp, or email. Customers can click and pay instantly—ideal for invoices and service-based businesses.",
  },
];

// ─── Validation ───────────────────────────────────────────────────────────────

const INITIAL_FORM_STATE = {
  fullname: "",
  business_name: "",
  mobile_number: "",
  email_address: "",
  business_type: "",
};

function validateForm(fields) {
  const errors = {};
  if (!fields.fullname.trim()) errors.fullname = "Full name is required.";
  if (!fields.business_name.trim())
    errors.business_name = "Business name is required.";

  const phoneRegex = /^[6-9]\d{9}$/;
  if (!phoneRegex.test(fields.mobile_number))
    errors.mobile_number = "Enter a valid 10-digit Indian mobile number.";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(fields.email_address))
    errors.email_address = "Enter a valid email address.";

  if (!fields.business_type)
    errors.business_type = "Please select a business type.";

  return errors;
}

// ─── Custom Hook ──────────────────────────────────────────────────────────────

function useLeadForm() {
  const [fields, setFields] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    // Clear field-level error on change
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const validationErrors = validateForm(fields);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(fields),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success("Thank you! We'll be in touch shortly.");
        setFields(INITIAL_FORM_STATE);
        setErrors({});
      } else {
        const message = result?.errors
          ? Object.values(result.errors).flat().join(" ")
          : result?.message || "Failed to submit. Please try again.";
        toast.error(message);
      }
    } catch {
      toast.error(
        "Something went wrong. Please check your connection and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [fields]);

  return { fields, errors, isSubmitting, handleChange, handleSubmit };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-red-500 text-sm mt-1">
      {message}
    </p>
  );
}

function LeadForm() {
  const { fields, errors, isSubmitting, handleChange, handleSubmit } =
    useLeadForm();

  const inputClass =
    "w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-blue-500 transition";
  const errorBorder = "border-red-400";
  const normalBorder = "border-gray-300";

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 text-black">
      <h3 className="text-2xl font-semibold mb-6 text-center">
        Get Started Today
      </h3>

      <div className="space-y-4" role="form" aria-label="Lead capture form">
        <div>
          <label htmlFor="fullname" className="sr-only">
            Full Name
          </label>
          <input
            id="fullname"
            type="text"
            name="fullname"
            placeholder="Full Name"
            value={fields.fullname}
            onChange={handleChange}
            className={`${inputClass} ${errors.fullname ? errorBorder : normalBorder}`}
            aria-invalid={!!errors.fullname}
            aria-describedby={errors.fullname ? "fullname-error" : undefined}
            autoComplete="name"
          />
          <FieldError message={errors.fullname} />
        </div>

        <div>
          <label htmlFor="business_name" className="sr-only">
            Business Name
          </label>
          <input
            id="business_name"
            type="text"
            name="business_name"
            placeholder="Business Name"
            value={fields.business_name}
            onChange={handleChange}
            className={`${inputClass} ${errors.business_name ? errorBorder : normalBorder}`}
            aria-invalid={!!errors.business_name}
            autoComplete="organization"
          />
          <FieldError message={errors.business_name} />
        </div>

        <div>
          <label htmlFor="mobile_number" className="sr-only">
            Mobile Number
          </label>
          <input
            id="mobile_number"
            type="tel"
            name="mobile_number"
            placeholder="Mobile Number"
            value={fields.mobile_number}
            onChange={handleChange}
            maxLength={10}
            inputMode="numeric"
            className={`${inputClass} ${errors.mobile_number ? errorBorder : normalBorder}`}
            aria-invalid={!!errors.mobile_number}
            autoComplete="tel"
          />
          <FieldError message={errors.mobile_number} />
        </div>

        <div>
          <label htmlFor="email_address" className="sr-only">
            Email Address
          </label>
          <input
            id="email_address"
            type="email"
            name="email_address"
            placeholder="Email Address"
            value={fields.email_address}
            onChange={handleChange}
            className={`${inputClass} ${errors.email_address ? errorBorder : normalBorder}`}
            aria-invalid={!!errors.email_address}
            autoComplete="email"
          />
          <FieldError message={errors.email_address} />
        </div>

        <div>
          <label htmlFor="business_type" className="sr-only">
            Business Type
          </label>
          <select
            id="business_type"
            name="business_type"
            value={fields.business_type}
            onChange={handleChange}
            className={`${inputClass} ${errors.business_type ? errorBorder : normalBorder}`}
            aria-invalid={!!errors.business_type}
          >
            <option value="">Business Type</option>
            {BUSINESS_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <FieldError message={errors.business_type} />
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-4 rounded-xl transition"
          aria-busy={isSubmitting}
        >
          {isSubmitting ? "Submitting…" : "Submit"}
        </button>
      </div>
    </div>
  );
}

const fadeUpVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easeInOut } },
};

// ─── Main Component ───────────────────────────────────────────────────────────

const UPIpayments = () => {
  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{ style: { zIndex: 9999 } }}
      />

      <Helmet>
        <meta charSet="utf-8" />
        <title>Payin service provider</title>
        <meta name="description" content="With Hoada payment"></meta>
        <meta
          name="description"
          content="Haoda Pay offers fast and secure Payin Solutions in India with T+0 settlements, UPI collections, and seamless payment processing for businesses."
        />
        <meta name="tags" content="payin solutions india, upi collection services" />
        <meta name="keywords" content="Payin service provider" />
      </Helmet>

      <Navbar />

      {/* Section 1 — Hero Banner */}
      <div className="UPIpayments__banner mt-16">
        <div className="UPIpayments__bannerVideo">
          <video loop autoPlay muted playsInline aria-hidden="true">
            <source src="/images/Web 800_1.mp4" type="video/mp4" />
          </video>

          <div className="UPIpayments__bannerTitle">
            <div className="container mx-auto px-4 py-20 flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2 text-center lg:text-left">
                <h1 className="UPIpayments__heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  Collect Payments Instantly with UPI
                </h1>
                <p className="mt-6 text-xl">
                  T+0 Settlements | Instant Setup | Secure API Integration
                </p>
                <p className="mt-4 text-lg max-w-lg mx-auto lg:mx-0">
                  Start accepting payments faster with Haoda Pay Payin service providers
                </p>
                <div className="mt-10">
                  <a
                    href="https://merchant.haodapay.com/login"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <button className="contact__btn flex items-center justify-center mx-auto lg:mx-0 gap-2 px-8 py-4 text-lg font-medium">
                      Sign Up
                      <FaArrowRight />
                    </button>
                  </a>
                </div>
              </div>

              <div className="lg:w-1/2 w-full max-w-md mx-auto">
                <LeadForm />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2 — Why Choose */}
      <div className="whyChoose__container py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={fadeUpVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-center mb-16">
              Reasons to Choose Haoda Pay UPI Collections
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {WHY_CHOOSE_ITEMS.map(({ title, desc }) => (
                <div
                  key={title}
                  className="bg-white p-8 rounded-2xl shadow hover:shadow-xl transition"
                >
                  <h3 className="font-semibold text-2xl mb-4 text-blue-600">
                    {title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Section 3 — Collection Modes */}
      <div className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={fadeUpVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-center mb-4">
              Multiple Collection Modes
            </h2>
            <div className="grid lg:grid-cols-2 gap-10 pt-4">
              {COLLECTION_MODES.map(({ title, desc }) => (
                <div key={title}>
                  <h3 className="text-2xl font-semibold mb-3">{title}</h3>
                  <p className="text-gray-600">{desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Section 4 — How It Works */}
      <div className="bg-gray-700 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">How It Works</h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {HOW_IT_WORKS_STEPS.map((step, index) => (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-gray-800 p-8 rounded-3xl flex flex-col items-center text-center border border-gray-700 hover:border-blue-500 transition shadow-lg"
              >
                <div
                  className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center mb-6 text-2xl font-bold"
                  aria-label={`Step ${index + 1}`}
                >
                  {index + 1}
                </div>
                <p className="text-lg leading-relaxed">{step}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-16 text-center max-w-3xl mx-auto"
          >
            <p className="text-gray-400 text-xl leading-relaxed">
              This simple process makes payments quicker, smoother, and more
              convenient—giving your customers a better overall experience.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Section 5 — Use Cases */}
      <div className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={fadeUpVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-center mb-6">Use Cases</h2>
            <p className="text-center text-xl text-gray-600 mb-12">
              Haoda Pay UPI Collections supports a wide range of industries
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {USE_CASES.map((useCase) => (
                <div
                  key={useCase}
                  className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-lg transition"
                >
                  <p className="font-medium text-lg">{useCase}</p>
                </div>
              ))}
            </div>
            <p className="text-center mt-12 text-gray-600 max-w-2xl mx-auto">
              Businesses can automate collections for{" "}
              <strong>
                EMIs, recurring payments, utility bills, subscriptions, and more
              </strong>
              , improving efficiency and reducing manual effort.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Section 6 — CTA */}
      <div className="joinHaodapay__revoluiton__bgColor py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-center mb-6">Get Started</h2>
          <p className="text-xl md:text-2xl mb-6">
            Upgrade your payment collection experience with Haoda Pay{" "}
            <strong>UPI Payin solutions</strong>
          </p>
          <a href="/contact">
            <button className="HaodaPay_Revolution_btn flex items-center justify-center gap-3 mx-auto px-10 py-5 text-lg">
              Contact Us Today
              <FaArrowRight />
            </button>
          </a>
        </div>
      </div>

      <Nextstep />
      <Footer />
    </>
  );
};

export default UPIpayments;
